import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';
import type { AdminMetricsResponse, CanaryStatus } from '../../services/api';
import { RefreshCw, Loader2, DollarSign, Users, Database, HardDrive, TrendingUp, Activity, ChevronRight } from 'lucide-react';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';

const CANARY_DOT: Record<CanaryStatus, string> = {
  green: 'bg-green-500', amber: 'bg-amber-500', red: 'bg-red-500', info: 'bg-gray-400',
};
const fmtPct = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : `${(n * 100).toFixed(1)}%`;

export function MetricsDashboard() {
  const [metrics, setMetrics] = useState<AdminMetricsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadMetrics = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getAdminMetrics();
      setMetrics(data);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to load metrics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadMetrics(); }, []);
  useAutoRefresh(loadMetrics);

  if (loading && !metrics) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Business Metrics</h1>
        <button
          onClick={loadMetrics}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg">{error}</div>
      )}

      {metrics && (
        <>
          {/* Key Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <MetricCard
              icon={<Users className="w-6 h-6 text-blue-500" />}
              label="Total Users"
              value={metrics.users?.total || 0}
              sub={`+${metrics.users?.new_users_7d || 0} this week`}
            />
            <MetricCard
              icon={<DollarSign className="w-6 h-6 text-green-500" />}
              label="Est. Monthly Revenue"
              value={`$${metrics.users?.monthly_revenue?.toFixed(2) || '0.00'}`}
              sub={`$${(metrics.users?.annual_revenue_projected || 0).toFixed(2)}/yr projected`}
            />
            <MetricCard
              icon={<TrendingUp className="w-6 h-6 text-purple-500" />}
              label="Total Jobs"
              value={metrics.growth?.total_jobs || 0}
              sub={`+${metrics.growth?.new_jobs_7d || 0} this week`}
            />
            <MetricCard
              icon={<Database className="w-6 h-6 text-orange-500" />}
              label="Total Resumes"
              value={metrics.growth?.total_resumes || 0}
            />
          </div>

          {/* Canary Health Summary */}
          {metrics.canaries && (
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Activity className="w-5 h-5" />
                  System Health — Canaries
                  <span className={`w-2.5 h-2.5 rounded-full ${CANARY_DOT[metrics.canaries.overall_status]}`} />
                </h2>
                <Link
                  to="/admin/f47ac10b58cc/canary-analytics"
                  className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  Full report <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                <CanaryTile
                  label="Ingest Health"
                  status={metrics.canaries.ingest.status}
                  value={fmtPct(metrics.canaries.ingest.ingest_success_rate)}
                  hint="URL → real card"
                />
                <CanaryTile
                  label="Outcome Funnel"
                  status={metrics.canaries.outcomes.status}
                  value={`${metrics.canaries.outcomes.applied.toLocaleString()} applied`}
                  hint={`${fmtPct(metrics.canaries.outcomes.error_rate)} gen errors`}
                />
                <CanaryTile
                  label="Unit Economics"
                  status={metrics.canaries.economics.status}
                  value={metrics.canaries.economics.cost_per_active_user === null
                    ? '—' : `$${metrics.canaries.economics.cost_per_active_user.toFixed(2)}`}
                  hint="cost / active user"
                />
                <CanaryTile
                  label="Conversion"
                  status={metrics.canaries.conversion.status}
                  value={fmtPct(metrics.canaries.conversion.paid_conversion_rate)}
                  hint={`${metrics.canaries.conversion.paid_users.toLocaleString()} paid`}
                />
                <CanaryTile
                  label="Trust & Errors"
                  status={metrics.canaries.trust.status}
                  value={`${metrics.canaries.trust.open_errors.toLocaleString()} open`}
                  hint={metrics.canaries.trust.fabrication_signals
                    ? `${metrics.canaries.trust.fabrication_signals} fabrication ⚠`
                    : `${metrics.canaries.trust.errors_7d} in 7d`}
                />
              </div>
            </div>
          )}

          {/* User Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Users by Role</h2>
              <div className="space-y-2">
                {Object.entries(metrics.users?.by_role || {}).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between">
                    <span className="capitalize text-gray-600">{type}</span>
                    <span className="font-medium">{count as number}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Users by Status</h2>
              <div className="space-y-2">
                {Object.entries(metrics.users?.by_status || {}).map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between">
                    <span className="capitalize text-gray-600">{status}</span>
                    <span className="font-medium">{count as number}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* DynamoDB Tables */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Database className="w-5 h-5" /> DynamoDB Tables
            </h2>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-4 text-gray-600 font-medium">Table</th>
                    <th className="text-right py-2 px-4 text-gray-600 font-medium">Items</th>
                    <th className="text-right py-2 px-4 text-gray-600 font-medium">Size</th>
                    <th className="text-center py-2 px-4 text-gray-600 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(metrics.dynamodb || {}).map(([name, data]) => (
                    <tr key={name} className="border-b hover:bg-gray-50">
                      <td className="py-2 px-4 font-mono text-sm">{name}</td>
                      <td className="py-2 px-4 text-right">{data.item_count.toLocaleString()}</td>
                      <td className="py-2 px-4 text-right">{(data.size_bytes / 1024).toFixed(1)} KB</td>
                      <td className="py-2 px-4 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          data.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {data.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* S3 Buckets */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <HardDrive className="w-5 h-5" /> S3 Buckets
            </h2>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-4 text-gray-600 font-medium">Bucket</th>
                    <th className="text-right py-2 px-4 text-gray-600 font-medium">Objects</th>
                    <th className="text-right py-2 px-4 text-gray-600 font-medium">Size</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(metrics.s3 || {}).map(([name, data]) => (
                    <tr key={name} className="border-b hover:bg-gray-50">
                      <td className="py-2 px-4 font-mono text-sm">{name}</td>
                      <td className="py-2 px-4 text-right">{data.object_count.toLocaleString()}</td>
                      <td className="py-2 px-4 text-right">{data.size_mb.toFixed(2)} MB</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Cost Estimate */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5" /> Monthly Cost Estimate
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(metrics.cost_estimate || {})
                .filter(([key]) => !['note', 'currency', 'total_estimate'].includes(key))
                .map(([service, cost]) => (
                  <div key={service} className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className="text-sm text-gray-500 capitalize">{service.replace('_', ' ')}</div>
                    <div className="text-lg font-semibold">${Number(cost).toFixed(2)}</div>
                  </div>
                ))}
            </div>
            <div className="mt-4 pt-4 border-t text-right">
              <span className="text-gray-600">Total Estimated: </span>
              <span className="text-xl font-bold">${metrics.cost_estimate?.total_estimate?.toFixed(2)}</span>
              <span className="text-gray-500"> /month</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function CanaryTile({ label, status, value, hint }: { label: string; status: CanaryStatus; value: string; hint?: string }) {
  return (
    <div className="p-3 bg-gray-50 rounded-lg">
      <div className="flex items-center gap-1.5 mb-1">
        <span className={`w-2 h-2 rounded-full ${CANARY_DOT[status]}`} />
        <span className="text-xs text-gray-500">{label}</span>
      </div>
      <div className="text-lg font-bold text-gray-800">{value}</div>
      {hint && <div className="text-xs text-gray-400">{hint}</div>}
    </div>
  );
}

function MetricCard({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string | number; sub?: string }) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center gap-3 mb-2">
        {icon}
        <span className="text-sm text-gray-500">{label}</span>
      </div>
      <div className="text-2xl font-bold text-gray-800">{value}</div>
      {sub && <div className="text-sm text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}
